import React, { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { clientService } from '../../application/ClientService';
import { vehicleService } from '../../application/VehicleService';
import { Client } from '../../domain/client/Client';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import { useRetrieveProject } from '../../hooks/project/useRetrieveProject';
import { AsyncSearchCustomOption, AsyncSearchNoOptionsMessage } from './AsyncSearchComponents';
import { Label as StyledLabel } from './FormFields.styled';

type FormLinkedElementProps = {
  type: `client` | `vehicle`;
  label?: string | React.ReactElement;
  projectId: string;
  fieldValue?: string;
  onChange: (...args: any[]) => any;
  placeholder: string | React.ReactElement;
  noOptionsMessage: string | React.ReactElement;
  isClearable?: boolean;
};

export const FormLinkedElement = ({
  type,
  label,
  projectId,
  fieldValue,
  onChange,
  placeholder,
  noOptionsMessage,
  isClearable = true,
}: FormLinkedElementProps) => {
  const { data: project } = useRetrieveProject({ projectId });
  const [value, setValue] = useState<Client | Vehicle | undefined>(undefined);
  useEffect(() => {
    (async () => {
      if (project && fieldValue) {
        let response: Client | Vehicle;
        switch (type) {
          case `client`:
            response = await clientService.fetchClient({ clientId: fieldValue, project: project! });
            break;
          case `vehicle`:
            response = await vehicleService.fetchVehicle({ vehicleId: fieldValue, project: project! });
            break;
        }
        if (response) {
          setValue(response);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, fieldValue, type]);

  const isSearchable = () => !value;

  const _loadSuggestions = (inputValue: string, callback: (options: any) => void) => {
    switch (type) {
      case `client`:
        clientService
          .searchClients({
            project: project!,
            offset: 0,
            limit: 5,
            searchText: inputValue,
          })
          .then((resp) => callback(resp.docs));
        break;
      case `vehicle`:
        vehicleService
          .searchVehicles({
            project: project!,
            offset: 0,
            limit: 5,
            searchText: inputValue,
          })
          .then((resp) => callback(resp.docs));
        break;
    }
  };

  const loadSuggestions = _.debounce(_loadSuggestions, 300);

  const onChangeValue = (val: any) => {
    setValue(val);
    onChange(val);
  };

  return (
    <>
      {label && <StyledLabel>{label}</StyledLabel>}
      <AsyncSelect
        placeholder={placeholder}
        value={value}
        cacheOptions
        isClearable={isClearable}
        isSearchable={isSearchable()}
        styles={{
          // input: (base) => ({
          //   fontSize: `13px`,
          // }),
          indicatorSeparator: () => ({
            display: `none`,
          }),
          control: (base, state) => ({
            ...base,
            color: RoutalPalette.text,
            fontSize: `13px`,
            boxShadow: `none`,
            borderColor: state.isFocused
              ? `${RoutalPalette.primary00} !important`
              : `${RoutalPalette.neutral20} !important`,
            backgroundColor: `transparent`,
            '&:hover': {
              backgroundColor: RoutalPalette.neutral05, // replace with your desired hover color
            },
          }),
          menu: (base) => ({
            ...base,
            color: RoutalPalette.text,
            backgroundColor: RoutalPalette.neutral00,
          }),
          clearIndicator: (base) => ({
            ...base,
            color: RoutalPalette.secondary60,
          }),
          dropdownIndicator: (base, state) => ({
            ...base,
            color: state.isFocused ? RoutalPalette.secondary60 : RoutalPalette.neutral20,
          }),
        }}
        components={{
          Option: AsyncSearchCustomOption,
          // IndicatorSeparator: {disabled: true},
          NoOptionsMessage: (_props: any) => <AsyncSearchNoOptionsMessage noOptionsMessage={noOptionsMessage} />,
        }}
        onChange={onChangeValue}
        loadOptions={loadSuggestions}
      />
    </>
  );
};
